"use strict"

class ProductPage {
  constructor(container) {
    if (!container) return;
    if (container.dataset.productPageInitialized === 'true') return;

    this.container = container;
    this.container.dataset.productPageInitialized = 'true';

    this.variantDataElement = this.container.querySelector('[data-product-variants]');
    this.variantData = this.variantDataElement ? JSON.parse(this.variantDataElement.textContent) : [];

    this.currentVariant = this.getInitialVariant();

    this.init();
  }

  init() {
    this.initGallerySwiper();
    this.initPackPicker();
    this.initAjaxAddToCart();
  }

  getInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = Number(urlParams.get('variant'));

    if (urlVariantId && this.variantData.length) {
      const matched = this.variantData.find((v) => v.id === urlVariantId);
      if (matched) return matched;
    }

    return this.variantData.find((v) => v.available) || this.variantData[0] || null;
  }

  // ─── SWIPER GALLERY ───────────────────────────────────────────

  initGallerySwiper() {
    if (typeof Swiper === 'undefined') return;

    const mainEl = this.container.querySelector('[data-product-swiper-main]');
    const thumbsEl = this.container.querySelector('[data-product-swiper-thumbs]');

    if (!mainEl) return;

    let thumbsSwiper = null;

    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        spaceBetween: 8,
        slidesPerView: 7,
        freeMode: true,
        watchSlidesProgress: true,
        breakpoints: {
          0: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          1024: { slidesPerView: 7 },
        },
      });
    }

    this.mainSwiper = new Swiper(mainEl, {
      spaceBetween: 0,
      slidesPerView: 1,
      loop: false,
      navigation: {
        prevEl: mainEl.querySelector('[data-swiper-prev]'),
        nextEl: mainEl.querySelector('[data-swiper-next]'),
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
    });

    this.thumbsSwiper = thumbsSwiper;

    if (this.currentVariant && this.currentVariant.featured_media) {
      this.slideToMedia(this.currentVariant.featured_media.id);
    }
  }

  slideToMedia(mediaId) {
    if (!this.mainSwiper) return;

    const slides = this.mainSwiper.slides;
    for (let i = 0; i < slides.length; i++) {
      if (String(slides[i].dataset.mediaId) === String(mediaId)) {
        this.mainSwiper.slideTo(i, 0);
        break;
      }
    }
  }

  // ─── PACK PICKER ──────────────────────────────────────────────

  initPackPicker() {
    this.packOptions = this.container.querySelectorAll('[data-pack-option]');
    this.packRadios = this.container.querySelectorAll('[data-pack-radio]');
    this.selectedLabel = this.container.querySelector('[data-pack-selected-label]');
    this.selectedPrice = this.container.querySelector('[data-pack-selected-price]');
    this.addToCartBtn = this.container.querySelector('[data-pack-add-to-cart]');
    this.buyNowBtn = this.container.querySelector('[data-pack-buy-now]');

    if (!this.packOptions.length) return;

    this.packOptions.forEach((option) => {
      option.addEventListener('click', () => {
        this.selectPackOption(option);
      });
    });
  }

  selectPackOption(option) {
    const pens = Number(option.dataset.pens) || 1;
    const packPrice = Number(option.dataset.packPrice) || 0;

    this.currentPens = pens;

    this.packOptions.forEach((opt) => {
      const isActive = opt === option;
      opt.classList.toggle('pack-picker__option--active', isActive);
      const radio = opt.querySelector('[data-pack-radio]');
      if (radio) radio.checked = isActive;
    });

    const pensWord = pens === 1 ? 'pen' : 'pens';

    if (this.selectedLabel) {
      this.selectedLabel.textContent = pens + ' ' + pensWord;
    }
    if (this.selectedPrice) {
      this.selectedPrice.textContent = option.dataset.priceDisplay || this.formatMoney(packPrice);
    }
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.pens = pens;
    }
    if (this.buyNowBtn) {
      const variantId = this.addToCartBtn ? this.addToCartBtn.dataset.variantId : '';
      this.buyNowBtn.href = '/cart/' + variantId + ':' + pens + '?checkout';
    }
  }

  // ─── ADD TO CART ──────────────────────────────────────────────

  initAjaxAddToCart() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener('click', async () => {
      const variantId = Number(this.addToCartBtn.dataset.variantId);
      const pens = Number(this.addToCartBtn.dataset.pens) || 1;
      if (!variantId) return;

      const initialText = this.addToCartBtn.textContent;
      try {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = 'Adding...';
        await addCartItem(variantId, pens);
        refreshCartUi({ openDrawer: true });
      } catch (error) {
        console.error('Pack picker add to cart error:', error);
      } finally {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = initialText;
      }
    });
  }

  // ─── HELPERS ──────────────────────────────────────────────────

  formatMoney(cents) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format((cents || 0) / 100);
  }
}

const initProductPages = (scope = document) => {
  const productSections = scope.querySelectorAll('[data-product-page]');
  productSections.forEach((section) => {
    new ProductPage(section);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initProductPages();
  initStickyCart();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductPages(event.target);
});

document.addEventListener('shopify:section:reorder', () => {
  initProductPages();
});



// STICKY CART (mobile only)

function initStickyCart() {
  const stickyCart = document.querySelector('[data-sticky-cart]');
  if (!stickyCart) return;

  const packPicker = document.querySelector('[data-pack-picker]');
  if (!packPicker) return;

  const options = stickyCart.querySelectorAll('[data-sticky-option]');
  const priceEl = stickyCart.querySelector('[data-sticky-price]');
  const addBtn = stickyCart.querySelector('[data-sticky-add-btn]');
  const variantId = addBtn?.dataset.variantId;
  let selectedPens = Number(addBtn?.dataset.pens) || 1;

  function updateVisibility() {
    if (window.innerWidth > 768) {
      stickyCart.classList.remove('is-visible');
      stickyCart.setAttribute('aria-hidden', 'true');
      return;
    }

    const pickerRect = packPicker.getBoundingClientRect();
    const pickerHeight = pickerRect.height;
    const visiblePortion = Math.min(Math.max(pickerRect.bottom, 0), pickerHeight);
    const visibleRatio = pickerHeight > 0 ? visiblePortion / pickerHeight : 1;
    const isPickerOutOfView = visibleRatio < 0.45;

    const footer = document.querySelector('footer') || document.querySelector('[data-section-type="footer"]');
    let isFooterVisible = false;
    if (footer) {
      const footerRect = footer.getBoundingClientRect();
      isFooterVisible = footerRect.top < window.innerHeight;
    }

    if (isPickerOutOfView && !isFooterVisible) {
      stickyCart.classList.add('is-visible');
      stickyCart.setAttribute('aria-hidden', 'false');
    } else {
      stickyCart.classList.remove('is-visible');
      stickyCart.setAttribute('aria-hidden', 'true');
    }
  }

  window.addEventListener('scroll', updateVisibility, { passive: true });
  window.addEventListener('resize', updateVisibility, { passive: true });
  updateVisibility();

  options.forEach(function (option) {
    option.addEventListener('click', function () {
      options.forEach(function (o) {
        o.classList.remove('sticky-cart__option--active');
      });
      option.classList.add('sticky-cart__option--active');

      selectedPens = Number(option.dataset.pens) || 1;
      const price = Number(option.dataset.packPrice);

      if (priceEl) {
        priceEl.textContent = option.dataset.priceDisplay || formatMoneyGBP(price);
      }
      if (addBtn) {
        addBtn.dataset.pens = selectedPens;
      }
    });
  });

  if (addBtn) {
    addBtn.addEventListener('click', async function () {
      if (!variantId) return;

      const initialText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';

      try {
        await addCartItem(Number(variantId), selectedPens);
        refreshCartUi({ openDrawer: true });
      } catch (err) {
        console.error('Sticky cart add error:', err);
      } finally {
        addBtn.disabled = false;
        addBtn.textContent = initialText;
      }
    });
  }

  const mainOptions = document.querySelectorAll('[data-pack-option]');
  mainOptions.forEach(function (mainOpt) {
    mainOpt.addEventListener('click', function () {
      const pens = mainOpt.dataset.pens;
      options.forEach(function (o) {
        if (o.dataset.pens === pens) {
          o.classList.add('sticky-cart__option--active');
        } else {
          o.classList.remove('sticky-cart__option--active');
        }
      });
      selectedPens = Number(pens) || 1;
      const price = Number(mainOpt.dataset.packPrice);
      if (priceEl) {
        priceEl.textContent = mainOpt.dataset.priceDisplay || formatMoneyGBP(price);
      }
      if (addBtn) {
        addBtn.dataset.pens = pens;
      }
    });
  });
}

function formatMoneyGBP(cents) {
  return (cents / 100).toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  });
}

// UPSELL

function parseUpsellVariantIds(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter(Boolean);
}

function glowBundledCartSectionPayload() {
  const ids = [];
  const drawer = document.querySelector('[data-cart-drawer-root]');
  if (drawer?.dataset.sectionId) {
    ids.push(drawer.dataset.sectionId);
  }

  const cartRoot = document.querySelector('[data-main-cart-root]');
  if (cartRoot?.dataset.sectionId) {
    ids.push(cartRoot.dataset.sectionId);
  }

  if (!ids.length) {
    return null;
  }

  const cartPath = window.__glowRoutes?.cartUrl || '/cart';
  const sections_url = cartPath.startsWith('/') ? cartPath : `/${cartPath}`;

  return {
    sections: ids.join(','),
    sections_url,
  };
}

function glowApplyBundledCartSections(result) {
  if (result?.sections && window.cartUi?.applyBundledSections) {
    window.cartUi.applyBundledSections(result.sections);
  }
}

async function getCartData() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to load cart');
  }

  return response.json();
}

async function addCartItem(variantId, quantity = 1, properties = {}) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: Number(variantId),
      quantity,
      properties,
      ...(glowBundledCartSectionPayload() || {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add item to cart');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function updateCartItemQuantity(lineKey, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      ...(glowBundledCartSectionPayload() || {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update cart item quantity');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function addUpsellGiftIfNeeded(section) {
  if (!section) return false;

  const productsWrap = section.querySelector('.product-upsell__products');
  if (!productsWrap) return false;

  const requiredVariantIds = parseUpsellVariantIds(productsWrap.dataset.requiredVariants);
  const giftVariantId = Number(section.dataset.giftVariantId);

  if (!requiredVariantIds.length || !giftVariantId) {
    return false;
  }

  const cart = await getCartData();
  const cartVariantIds = cart.items.map((item) => Number(item.variant_id));

  const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
    cartVariantIds.includes(variantId)
  );

  const hasGiftAlready = cartVariantIds.includes(giftVariantId);

  if (!hasAllRequiredProducts || hasGiftAlready) {
    return false;
  }

  await addCartItem(giftVariantId, 1, {
    upsell_gift: 'true',
    upsell_required_variant_ids: requiredVariantIds.join(','),
  });

  return true;
}

function refreshCartUi(detail = {}) {
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
}

async function removeInvalidUpsellGiftsIfNeeded() {
  const cart = await getCartData();
  const cartVariantIds = new Set(cart.items.map((item) => Number(item.variant_id)));
  const giftLineKeysToRemove = [];

  cart.items.forEach((item) => {
    const properties = item.properties || {};
    const isUpsellGift =
      String(properties.upsell_gift).toLowerCase() === 'true'
      || String(properties._upsell_gift).toLowerCase() === 'true';
    if (!isUpsellGift) return;

    const requiredVariantIds = parseUpsellVariantIds(
      properties.upsell_required_variant_ids || properties._upsell_required_variant_ids
    );
    if (!requiredVariantIds.length) return;

    const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
      cartVariantIds.has(variantId)
    );

    if (!hasAllRequiredProducts) {
      giftLineKeysToRemove.push(item.key);
    }
  });

  if (!giftLineKeysToRemove.length) return false;

  await Promise.all(giftLineKeysToRemove.map((lineKey) => updateCartItemQuantity(lineKey, 0)));
  return true;
}

let isUpsellGiftSyncInProgress = false;

async function syncUpsellGiftState() {
  if (isUpsellGiftSyncInProgress) return;

  isUpsellGiftSyncInProgress = true;
  try {
    const wasGiftRemoved = await removeInvalidUpsellGiftsIfNeeded();
    if (wasGiftRemoved) {
      refreshCartUi();
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    }
  } catch (error) {
    console.error('Upsell gift sync error:', error);
  } finally {
    isUpsellGiftSyncInProgress = false;
  }
}

function setAjaxButtonLoadingState(button, loading, loadingText = 'Adding to cart...') {
  if (!button) return;

  if (!button.dataset.initialText) {
    button.dataset.initialText = button.textContent.trim();
  }

  if (loading) {
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.textContent = loadingText;
  } else {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
    button.textContent = button.dataset.initialText;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('.product-upsell .product-upsell__card-button');
  if (!button) return;

  event.preventDefault();

  const section = button.closest('.product-upsell');
  const card = button.closest('.product-upsell__card');
  const variantId = Number(button.dataset.variantId || card?.dataset.variantId);

  if (!section || !variantId) return;

  try {
    setAjaxButtonLoadingState(button, true);

    await addCartItem(variantId);

    await addUpsellGiftIfNeeded(section);

    refreshCartUi({ openDrawer: true });
  } catch (error) {
    console.error('Upsell add to cart error:', error);
  } finally {
    setAjaxButtonLoadingState(button, false);
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-ajax-add-to-cart-form]');
  if (!form) return;

  event.preventDefault();

  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const submitButton = form.querySelector('[data-ajax-add-to-cart-button]');
  const variantId = Number(variantInput?.value);
  const quantity = Number(quantityInput?.value || 1);

  if (!variantId) return;

  try {
    setAjaxButtonLoadingState(submitButton, true);
    await addCartItem(variantId, quantity > 0 ? quantity : 1);
    refreshCartUi({ openDrawer: true });
  } catch (error) {
    console.error('Add to cart form error:', error);
  } finally {
    setAjaxButtonLoadingState(submitButton, false);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  syncUpsellGiftState();
});

document.addEventListener('cart:refresh', () => {
  syncUpsellGiftState();
});


// ACCORDION

function initProductAccordion(scope = document) {
  const accordions = scope.querySelectorAll('[data-product-accordion]');

  accordions.forEach((accordion) => {
    if (accordion.dataset.accordionInitialized === 'true') return;
    accordion.dataset.accordionInitialized = 'true';

    const items = accordion.querySelectorAll('.product-accordion__item');

    let hasOpenedItem = false;

    items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const panel = item.querySelector('[data-accordion-panel]');

      if (!trigger || !panel) return;

      panel.hidden = false;
      panel.style.overflow = 'hidden';

      if (item.classList.contains('is-open') && !hasOpenedItem) {
        hasOpenedItem = true;
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = 'auto';
      } else {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = '0px';
      }
    });

    accordion.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const item = trigger.closest('.product-accordion__item');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');

      items.forEach((accordionItem) => {
        if (accordionItem !== item && accordionItem.classList.contains('is-open')) {
          closeAccordionItem(accordionItem);
        }
      });

      if (isOpen) {
        closeAccordionItem(item);
      } else {
        openAccordionItem(item);
      }
    });
  });
}

function openAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  item.classList.add('is-open');
  trigger.setAttribute('aria-expanded', 'true');

  panel.style.height = `${panel.scrollHeight}px`;

  const onTransitionEnd = (event) => {
    if (event.propertyName !== 'height') return;

    if (item.classList.contains('is-open')) {
      panel.style.height = 'auto';
    }

    panel.removeEventListener('transitionend', onTransitionEnd);
  };

  panel.addEventListener('transitionend', onTransitionEnd);
}

function closeAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  panel.style.height = `${panel.scrollHeight}px`;
  panel.offsetHeight;

  item.classList.remove('is-open');
  trigger.setAttribute('aria-expanded', 'false');

  requestAnimationFrame(() => {
    panel.style.height = '0px';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductAccordion();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductAccordion(event.target);
});
